import type {
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

export interface Specialty {
  id: number;
  name: string;
}

export interface Procedure {
  procedure_id: number;
  procedure_name: string;
}

export interface Practice {
  id: number;
  locId: string;
  practiceName: string;
}

export interface Specialist {
  specialist_id: number;
  specialist_specId: string;
  specialist_name: string;
  specialty_id: number;
  specialty_name: string;
}

export interface PracticeBySpecialty {
  practice_id: number;
  practice_locId: string;
  practice_name: string;
  referral_practice_address?: string;
  referral_practice_phone?: string;
  specialist_id: number;
  specialist_specId: string;
}

export interface ReferralDetailsResponse {
  id: string;
  practice_id: string;
  unified_appointment_id: string;
  estimated_visit_id?: number;
  patient_first_name?: string;
  patient_last_name?: string;
  patient_email?: string;
  patient_phone?: string;
  referral_reason?: string;
  tooth_numbers?: string[];
  specialty_id?: number;
  specialty_name?: string;
  procedures_ids?: number[];
  procedures: {
    id: number;
    name: string;
  }[];
  referral_practice_id?: number;
  referral_practice_locId?: string;
  referral_specialist_id?: number;
  referral_specialist_specId?: string;
  narrative?: string;
  image_urls?: string[];
  status: string;
  current_step?: string;
  created_at: string;
  updated_at: string;
  // Additional fields from the API response
  dentrix_contact?: {
    email: string;
    home_phone: string;
    work_phone: string;
  };
  dentrix_email?: string;
  dentrix_home_phone?: string;
  dentrix_work_phone?: string;
  analyses?: any[];
}

interface ReferralInfoResponse {
  practiceName: string;
  practiceLocation: string;
  practiceEmail: string;
  practicePhoneNumber: string;
  provider: {
    firstName: string;
    lastName: string;
  } | null;
  patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    email?: string;
    phone?: string;
  };
}

type QueryKey = [
  'practice',
  string,
  'patient',
  string | undefined,
  string | undefined,
];

export const useReferralInfo = (
  practiceId: string,
  pmsPatientVideaId?: string,
  imagingPatientVideaId?: string,
  options?: Omit<
    UseQueryOptions<
      ReferralInfoResponse,
      unknown,
      ReferralInfoResponse,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  // Only validate if the query is going to be enabled
  const isEnabled = options?.enabled !== false;
  if (isEnabled && !pmsPatientVideaId && !imagingPatientVideaId) {
    throw new Error(
      'Either pmsPatientVideaId or imagingPatientVideaId must be provided',
    );
  }

  return useQuery<
    ReferralInfoResponse,
    unknown,
    ReferralInfoResponse,
    QueryKey
  >(
    [
      'practice',
      practiceId,
      'patient',
      pmsPatientVideaId,
      imagingPatientVideaId,
    ],
    () =>
      makeAPIRequestReactQuery<ReferralInfoResponse>(
        `${endpoints.referralsPractice}/${practiceId}/patient`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
          params: {
            pmsPatientId: pmsPatientVideaId,
            imagingPatientId: imagingPatientVideaId,
          },
        },
      ),
    {
      enabled: !!practiceId && (!!pmsPatientVideaId || !!imagingPatientVideaId),
      ...options,
    },
  );
};

export const useReferralDetails = (
  unifiedAppointmentId: string,
  options?: Omit<
    UseQueryOptions<
      ReferralDetailsResponse,
      unknown,
      ReferralDetailsResponse,
      ['referral', string, 'details']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  return useQuery<
    ReferralDetailsResponse,
    unknown,
    ReferralDetailsResponse,
    ['referral', string, 'details']
  >(
    ['referral', unifiedAppointmentId, 'details'],
    () =>
      makeAPIRequestReactQuery<ReferralDetailsResponse>(
        `${endpoints.referrals}/${unifiedAppointmentId}/details`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!unifiedAppointmentId,
      ...options,
    },
  );
};

export const useReferralDetailsByReferralId = (
  referralId: string,
  options?: Omit<
    UseQueryOptions<
      ReferralDetailsResponse,
      unknown,
      ReferralDetailsResponse,
      ['referral', 'by-referral-id', string, 'details']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  return useQuery<
    ReferralDetailsResponse,
    unknown,
    ReferralDetailsResponse,
    ['referral', 'by-referral-id', string, 'details']
  >(
    ['referral', 'by-referral-id', referralId, 'details'],
    () =>
      makeAPIRequestReactQuery<ReferralDetailsResponse>(
        `${endpoints.referralsByReferralId}/${referralId}/details`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!referralId,
      ...options,
    },
  );
};

export const useSpecialties = (
  practiceId?: string,
  options?: Omit<
    UseQueryOptions<
      Specialty[],
      unknown,
      Specialty[],
      ['referrals', 'specialties', string | undefined]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login?.user || {},
  );

  return useQuery<
    Specialty[],
    unknown,
    Specialty[],
    ['referrals', 'specialties', string | undefined]
  >(
    ['referrals', 'specialties', practiceId],
    () =>
      makeAPIRequestReactQuery<Specialty[]>(
        endpoints.referralsSpecialties,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
          params: {
            practiceId,
          },
        },
      ),
    {
      enabled: !!orgId, // Only enable the query if orgId is available
      staleTime: 1000 * 60 * 60, // Cache for 1 hour since specialties rarely change
      ...options,
    },
  );
};

export const useProceduresBySpecialty = (
  specialtyId: number,
  options?: Omit<
    UseQueryOptions<
      Procedure[],
      unknown,
      Procedure[],
      ['referrals', 'specialties', number, 'procedures']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login?.user || {},
  );

  return useQuery<
    Procedure[],
    unknown,
    Procedure[],
    ['referrals', 'specialties', number, 'procedures']
  >(
    ['referrals', 'specialties', specialtyId, 'procedures'],
    () =>
      makeAPIRequestReactQuery<Procedure[]>(
        `${endpoints.referralsSpecialties}/${specialtyId}/procedures`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!specialtyId && !!orgId, // Only enable if both specialtyId and orgId are available
      staleTime: 1000 * 60 * 30, // Cache for 30 minutes
      ...options,
    },
  );
};

export const useProceduresBySpecialtyName = (
  specialtyName: string,
  options?: Omit<
    UseQueryOptions<
      Procedure[],
      unknown,
      Procedure[],
      ['referrals', 'specialties', 'name', string, 'procedures']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login?.user || {},
  );

  return useQuery<
    Procedure[],
    unknown,
    Procedure[],
    ['referrals', 'specialties', 'name', string, 'procedures']
  >(
    ['referrals', 'specialties', 'name', specialtyName, 'procedures'],
    () =>
      makeAPIRequestReactQuery<Procedure[]>(
        `${endpoints.referralsSpecialties}/name/${encodeURIComponent(
          specialtyName,
        )}/procedures`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!specialtyName && !!orgId, // Only enable if both specialtyName and orgId are available
      staleTime: 1000 * 60 * 30, // Cache for 30 minutes
      ...options,
    },
  );
};

export const usePractices = (
  options?: Omit<
    UseQueryOptions<
      Practice[],
      unknown,
      Practice[],
      ['referrals', 'practices']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login?.user || {},
  );

  return useQuery<Practice[], unknown, Practice[], ['referrals', 'practices']>(
    ['referrals', 'practices'],
    () =>
      makeAPIRequestReactQuery<Practice[]>(
        endpoints.referralsPractices,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!orgId, // Only enable the query if orgId is available
      staleTime: 1000 * 60 * 60, // Cache for 1 hour since practices rarely change
      ...options,
    },
  );
};

export const useSpecialists = (
  options?: Omit<
    UseQueryOptions<
      Specialist[],
      unknown,
      Specialist[],
      ['referrals', 'specialists']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login?.user || {},
  );

  return useQuery<
    Specialist[],
    unknown,
    Specialist[],
    ['referrals', 'specialists']
  >(
    ['referrals', 'specialists'],
    () =>
      makeAPIRequestReactQuery<Specialist[]>(
        endpoints.referralsSpecialists,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!orgId, // Only enable the query if orgId is available
      staleTime: 1000 * 60 * 30, // Cache for 30 minutes
      ...options,
    },
  );
};

export const usePracticesBySpecialty = (
  specialtyId: number,
  options?: Omit<
    UseQueryOptions<
      PracticeBySpecialty[],
      unknown,
      PracticeBySpecialty[],
      ['referrals', 'specialties', number, 'practices']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId, primaryPracticeId: practiceId } =
    useSelector((state: any) => state.login?.user || {});

  return useQuery<
    PracticeBySpecialty[],
    unknown,
    PracticeBySpecialty[],
    ['referrals', 'specialties', number, 'practices']
  >(
    ['referrals', 'specialties', specialtyId, 'practices'],
    () =>
      makeAPIRequestReactQuery<PracticeBySpecialty[]>(
        `${endpoints.referralsSpecialties}/${specialtyId}/practices`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
          params: {
            practiceId,
          },
        },
      ),
    {
      enabled: !!specialtyId && !!orgId && !!practiceId, // Only enable if specialtyId, orgId, and practiceId are available
      staleTime: 1000 * 60 * 30, // Cache for 30 minutes
      ...options,
    },
  );
};

export const useSpecialistsByPractice = (
  practiceId: number,
  options?: Omit<
    UseQueryOptions<
      Specialist[],
      unknown,
      Specialist[],
      ['referrals', 'practices', number, 'specialists']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login?.user || {},
  );

  return useQuery<
    Specialist[],
    unknown,
    Specialist[],
    ['referrals', 'practices', number, 'specialists']
  >(
    ['referrals', 'practices', practiceId, 'specialists'],
    () =>
      makeAPIRequestReactQuery<Specialist[]>(
        `${endpoints.referralsPractices}/${practiceId}/specialists`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!practiceId && !!orgId, // Only enable if both practiceId and orgId are available
      staleTime: 1000 * 60 * 30, // Cache for 30 minutes
      ...options,
    },
  );
};

interface CreateReferralRequest {
  practiceId: string;
  unifiedAppointmentId: string;
  estimatedVisitId?: number;
  analysisIds?: string[];
  patientFirstName?: string;
  patientLastName?: string;
  patientEmail?: string;
  patientPhone?: string;
  patientDateOfBirth?: string;
  referralReason?: string;
  toothNumbers?: string[];
  specialtyId?: number;
  proceduresIds?: number[];
  referralPracticeId?: number;
  referralSpecialistId?: number;
  useNextAvailableSpecialist?: boolean;
  doctorFirstName?: string;
  doctorLastName?: string;
  doctorPhone?: string;
  practiceEmail?: string;
  narrative?: string;
  imageUrls?: string[];
  status?: 'in_progress' | 'submitted' | 'needs_referral' | 'not_started';
}

export interface UpdateReferralRequest extends Partial<CreateReferralRequest> {
  status?: 'in_progress' | 'submitted' | 'needs_referral' | 'not_started';
}

interface ReferralStatusResponse {
  status: string;
  hasRequestData?: boolean;
}

export const useCreateReferral = (
  options?: UseMutationOptions<{ id: string }, unknown, CreateReferralRequest>,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  return useMutation<{ id: string }, unknown, CreateReferralRequest>(
    (data) =>
      makeAPIRequestReactQuery<{ id: string }>(
        endpoints.referrals,
        undefined,
        'post',
        {
          headers: {
            Org: orgId,
            'Content-Type': 'application/json',
          },
          data,
        },
      ),
    options,
  );
};

export const useUpdateReferral = (
  unifiedAppointmentId: string,
  options?: UseMutationOptions<unknown, unknown, UpdateReferralRequest>,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  return useMutation<unknown, unknown, UpdateReferralRequest>(
    (data) =>
      makeAPIRequestReactQuery(
        `${endpoints.referrals}/${unifiedAppointmentId}`,
        undefined,
        'put',
        {
          headers: {
            Org: orgId,
            'Content-Type': 'application/json',
          },
          data,
        },
      ),
    options,
  );
};

export const useUpdateReferralById = (
  options?: UseMutationOptions<
    unknown,
    unknown,
    { referralId: string; data: UpdateReferralRequest }
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  return useMutation<
    unknown,
    unknown,
    { referralId: string; data: UpdateReferralRequest }
  >(
    ({ referralId, data }) =>
      makeAPIRequestReactQuery(
        `${endpoints.referralsByReferralId}/${referralId}`,
        undefined,
        'put',
        {
          headers: {
            Org: orgId,
            'Content-Type': 'application/json',
          },
          data,
        },
      ),
    options,
  );
};

export const useReferralStatus = (
  unifiedAppointmentId: string,
  options?: Omit<
    UseQueryOptions<ReferralStatusResponse, unknown, ReferralStatusResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  return useQuery<ReferralStatusResponse, unknown, ReferralStatusResponse>(
    ['referral', unifiedAppointmentId, 'status'],
    () =>
      makeAPIRequestReactQuery<ReferralStatusResponse>(
        `${endpoints.referrals}/${unifiedAppointmentId}/status`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!unifiedAppointmentId,
      ...options,
    },
  );
};

export interface ResubmitReferralResponse {
  success: boolean;
  create_message: string;
  oralinxId?: string;
}

export const useResubmitReferral = (
  options?: UseMutationOptions<ResubmitReferralResponse, unknown, string>,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  return useMutation<ResubmitReferralResponse, unknown, string>(
    (referralId) =>
      makeAPIRequestReactQuery<ResubmitReferralResponse>(
        `${endpoints.referrals}/${referralId}/resubmit`,
        undefined,
        'post',
        {
          headers: {
            Org: orgId,
            'Content-Type': 'application/json',
          },
        },
      ),
    options,
  );
};

export const useDeleteReferralById = (
  options?: UseMutationOptions<{ success: boolean }, unknown, string>,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login.user,
  );

  return useMutation<{ success: boolean }, unknown, string>(
    (referralId) =>
      makeAPIRequestReactQuery<{ success: boolean }>(
        `${endpoints.referralsByReferralId}/${referralId}`,
        undefined,
        'delete',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    options,
  );
};

// New location-based search interfaces and hook
export interface LocationSearchSpecialist {
  id: number;
  specId: string;
  specialistName: string;
  specialtyId: number;
  specialtyName: string;
}

export interface LocationSearchPractice {
  id: number;
  locId: string;
  practiceName: string;
  address: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
  distance: number;
  specialists: LocationSearchSpecialist[];
}

export interface LocationSearchResponse {
  data: LocationSearchPractice[];
  total_practices: number;
  total_specialists: number;
  offset: number;
  limit: number | null;
}

interface UseSearchByLocationParams {
  postalCode: string;
  radiusKm?: number;
  specialtyIds?: number[];
  offset?: number;
  limit?: number;
}

export const useSearchByLocation = (
  params: UseSearchByLocationParams,
  options?: Omit<
    UseQueryOptions<
      LocationSearchResponse,
      unknown,
      LocationSearchResponse,
      ['referrals', 'search-by-location', UseSearchByLocationParams]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login?.user || {},
  );

  const { postalCode, radiusKm, specialtyIds, offset, limit } = params;

  return useQuery<
    LocationSearchResponse,
    unknown,
    LocationSearchResponse,
    ['referrals', 'search-by-location', UseSearchByLocationParams]
  >(
    ['referrals', 'search-by-location', params],
    () => {
      const queryParams: Record<string, any> = {
        postal_code: postalCode,
      };

      if (radiusKm !== undefined) {
        queryParams.radius_km = radiusKm;
      }
      if (specialtyIds && specialtyIds.length > 0) {
        queryParams.specialty_ids = specialtyIds;
      }
      if (offset !== undefined) {
        queryParams.offset = offset;
      }
      if (limit !== undefined) {
        queryParams.limit = limit;
      }

      return makeAPIRequestReactQuery<LocationSearchResponse>(
        `${endpoints.referrals}/search-by-location`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
          params: queryParams,
          paramsSerializer: (params) => {
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                // For arrays, add each value as a separate parameter with the same key
                value.forEach((item) => searchParams.append(key, String(item)));
              } else if (value !== undefined && value !== null) {
                searchParams.append(key, String(value));
              }
            });
            return searchParams.toString();
          },
        },
      );
    },
    {
      enabled: !!orgId && !!postalCode && postalCode.length >= 5,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
      ...options,
    },
  );
};

export interface PracticePostalCodeResponse {
  postalCode: string | null;
}

export const usePracticePostalCode = (
  practiceId: string,
  options?: Omit<
    UseQueryOptions<
      PracticePostalCodeResponse,
      unknown,
      PracticePostalCodeResponse,
      ['practices', string, 'postal-code']
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { primaryOrganizationId: orgId } = useSelector(
    (state: any) => state.login?.user || {},
  );

  return useQuery<
    PracticePostalCodeResponse,
    unknown,
    PracticePostalCodeResponse,
    ['practices', string, 'postal-code']
  >(
    ['practices', practiceId, 'postal-code'],
    () =>
      makeAPIRequestReactQuery<PracticePostalCodeResponse>(
        `${endpoints.practices}/${practiceId}/postal-code`,
        undefined,
        'get',
        {
          headers: {
            Org: orgId,
          },
        },
      ),
    {
      enabled: !!practiceId && !!orgId,
      staleTime: 1000 * 60 * 60, // Cache for 1 hour since practice postal code rarely changes
      ...options,
    },
  );
};
